import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  cancelOrder,
  createChangeRequest,
  createOrder,
  CreateOrderInput,
  getOrder,
  OrderLineInput,
  repeatOrder,
  submitOrder,
  updateOrder,
  UpdateOrderInput,
} from './api';

export function useOrder(orderId: string | undefined) {
  return useQuery({
    queryKey: ['order', orderId],
    queryFn: () => getOrder(orderId!),
    enabled: orderId !== undefined,
  });
}

/** Every write invalidates the list and, when known, the single-order query — `HomePage`/`OrderListPage` always reflect the latest state instead of a stale cache. */
function useInvalidateOrders() {
  const queryClient = useQueryClient();

  return (orderId?: string) => {
    void queryClient.invalidateQueries({ queryKey: ['orders'] });

    if (orderId !== undefined) {
      void queryClient.invalidateQueries({ queryKey: ['order', orderId] });
    }
  };
}

export function useCreateOrder() {
  const invalidate = useInvalidateOrders();

  return useMutation({
    mutationFn: (input: CreateOrderInput) => createOrder(input),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateOrder(orderId: string) {
  const invalidate = useInvalidateOrders();

  return useMutation({
    mutationFn: (input: UpdateOrderInput) => updateOrder(orderId, input),
    onSuccess: () => invalidate(orderId),
  });
}

/** Takes the id per call: the form submits an order it created a moment ago, so the id is not known when the hook mounts. */
export function useSubmitOrder() {
  const invalidate = useInvalidateOrders();

  return useMutation({
    mutationFn: (orderId: string) => submitOrder(orderId),
    onSuccess: (order) => invalidate(order.id),
  });
}

export function useCancelOrder(orderId: string) {
  const invalidate = useInvalidateOrders();

  return useMutation({
    mutationFn: (reason: string) => cancelOrder(orderId, reason),
    onSuccess: () => invalidate(orderId),
  });
}

/** Never creates anything by itself (`portal-orders/spec.md` → "sin crear el pedido hasta que el usuario confirme"); it only fetches the previous order's lines to preload the form. */
export function useRepeatOrder() {
  return useMutation({
    mutationFn: ({
      siteId,
      serviceDate,
      operatingUnitId,
    }: {
      siteId: string;
      serviceDate: string;
      operatingUnitId?: string;
    }) => repeatOrder(siteId, serviceDate, operatingUnitId),
  });
}

/** `portal-orders/spec.md` → "Solicitud de cambio post-corte creada desde el portal, aprobada siempre en back-office": the response never offers an approve/reject action here — no role on the portal has `order.approve.change`. */
export function useCreateChangeRequest(orderId: string) {
  const invalidate = useInvalidateOrders();

  return useMutation({
    mutationFn: (items: OrderLineInput[]) => createChangeRequest(orderId, items),
    onSuccess: () => invalidate(orderId),
  });
}
